function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function asBoolean(value: unknown): boolean {
  return value === true
}

function truncate(value: string, maxLength = 120): string {
  if (value.length <= maxLength) {
    return value
  }
  return `${value.slice(0, maxLength - 1)}…`
}

function joinParts(parts: Array<string | null | undefined>): string | null {
  const filtered = parts.filter((part): part is string => Boolean(part))
  return filtered.length > 0 ? filtered.join(' · ') : null
}

function formatRetryCount(details: Record<string, unknown>): string | null {
  const retryCount = asNumber(details.retry_count)
  const maxRetries = asNumber(details.max_retries)
  if (retryCount == null || maxRetries == null) {
    return null
  }
  return `retry ${retryCount}/${maxRetries}`
}

function formatRetryPass(details: Record<string, unknown>): string | null {
  const passCount = asNumber(details.retry_pass_count)
  const maxPasses = asNumber(details.max_retry_passes)
  if (passCount == null || maxPasses == null) {
    return null
  }
  return `pass ${passCount}/${maxPasses}`
}

function formatPageNumber(details: Record<string, unknown>): string | null {
  const pageNumber = asNumber(details.page_number)
  return pageNumber == null ? null : `page ${pageNumber}`
}

function formatFallbackDetails(details: Record<string, unknown>): string | null {
  const parts = Object.entries(details)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return `${key}=${value}`
      }
      return null
    })
    .filter((part): part is string => part != null)

  return parts.length > 0 ? truncate(parts.join(' · '), 160) : null
}

export function formatJobEventDetails(
  eventType: string,
  details: Record<string, unknown>,
): string | null {
  if (!details || Object.keys(details).length === 0) {
    return null
  }

  switch (eventType) {
    case 'page_started':
      return formatPageNumber(details)

    case 'page_completed': {
      const page = formatPageNumber(details)
      const flags = [
        asBoolean(details.replay) ? 'replay' : null,
        asBoolean(details.was_failed) ? 'was failed' : null,
      ]
      return joinParts([page, ...flags])
    }

    case 'page_failed':
      return joinParts([
        formatPageNumber(details),
        asString(details.error_message) ? truncate(details.error_message as string) : null,
      ])

    case 'job_started': {
      const expectedPages = asNumber(details.expected_pages)
      return expectedPages == null ? null : `${expectedPages} expected pages`
    }

    case 'lease_acquired':
      return joinParts([
        asString(details.lease_holder),
        asNumber(details.lease_duration_seconds) != null
          ? `${details.lease_duration_seconds}s`
          : null,
      ])

    case 'lease_released':
      return asString(details.reason)

    case 'job_completed': {
      const parts: Array<string | null> = []
      if (asBoolean(details.partial_result)) {
        parts.push('partial result')
      }
      if (asBoolean(details.truncation_severe)) {
        parts.push('truncation severe')
      }
      if (asBoolean(details.has_unrecovered_truncation)) {
        parts.push('unrecovered truncation')
      }
      const pagesWithTruncation = asNumber(details.pages_with_unrecovered_truncation)
      if (pagesWithTruncation != null && pagesWithTruncation > 0) {
        parts.push(`${pagesWithTruncation} pages with truncation`)
      }
      const totalRegions = asNumber(details.total_unrecovered_truncation_regions)
      if (totalRegions != null && totalRegions > 0) {
        parts.push(`${totalRegions} truncation regions`)
      }
      return joinParts(parts)
    }

    case 'job_failed':
      return joinParts([
        asString(details.error_type),
        asString(details.error_message) ? truncate(details.error_message as string) : null,
      ])

    case 'retry_queued':
    case 'retry_published':
    case 'retry_publish_failed':
    case 'retry_publish_failed_terminal':
      return joinParts([
        asString(details.reason),
        formatRetryCount(details),
        asString(details.error_message) ? truncate(details.error_message as string) : null,
      ])

    case 'reaped_failed':
      return joinParts([asString(details.reason), formatRetryCount(details)])

    case 'retry_pass_pending':
      return joinParts([
        asNumber(details.retriable_failed_count) != null
          ? `${details.retriable_failed_count} retriable`
          : null,
        formatRetryPass(details),
      ])

    case 'retry_pass_scheduled':
      return joinParts([
        asNumber(details.pages_reset) != null ? `${details.pages_reset} pages reset` : null,
        formatRetryPass(details),
      ])

    case 'retry_pass_publish_failed':
      return joinParts([
        formatRetryPass(details),
        asString(details.error_message) ? truncate(details.error_message as string) : null,
      ])

    case 'dlq_terminal':
      return asString(details.error_message)
        ? truncate(details.error_message as string)
        : null

    default:
      return formatFallbackDetails(details)
  }
}
